<template>
  <div>
    <b-overlay
      :show="serversResponses('serversList') && serversEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            Servers
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Servers</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
        <div class="ml-auto">
          <b-button
            v-wave
            size="sm"
            variant="outline-secondary"
            class="align-middle mr-4"
            :disabled="serversResponses('getOpenSshKey')"
            @click="showModal('openSshKey')">
            <i class="fa fa-key fa-sm p-0 pr-sm-2"></i>
            <span class="d-none d-sm-inline">OpenSSH Public Key</span>
          </b-button>
          <b-button
            v-wave
            size="sm"
            variant="primary"
            class="align-middle btn-shadow"
            :disabled="recipesResponses('recipesList') || recipeGroupsResponses('recipesGroupsList')"
            @click="unselectServer(), showModal('manageServerModal')">
            <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
            <span class="d-none d-sm-inline">Add New Server</span>
          </b-button>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border">

        <b-card-body>
          <b-table
            :items="serversList"
            :fields="visibleFields"
            :class="{ 'table-hover' : !serversEmpty }"
            :tbody-tr-class="(item, type) => item && item.visibility ? 'default' : 'muted'"
            stacked="lg"
            head-variant=""
            show-empty
            responsive>
            
            <!-- custom headings -->
            <template #head(name)="data">
              <sort-button
                :data="data"
                :getParams="serversGetParams"
                @change="key => handleSortDirection(key)" />
            </template>
            <template #head(country)="data">
              <sort-button
                :data="data"
                :getParams="serversGetParams"
                @change="key => handleSortDirection(key)" />
            </template>
            <template #head(ip)="data">
              <sort-button
                :data="data"
                :getParams="serversGetParams"
                @change="key => handleSortDirection(key)"
                class="ml-2" />
            </template>
            <template #head(client_version)="data">
              <span
                v-b-tooltip.hover
                class="cursor-help"
                title="Client Version">
                {{ data.label }}
              </span>
            </template>

            <!-- custom cells -->
            <template #cell(name)="data">
              <div class="d-flex align-items-center">
                <i
                  v-if="!data.item.visibility"
                  v-b-tooltip.hover
                  title="Server is hidden from users and resellers"
                  class="fas fa-eye-slash fa-sm mr-2 cursor-help"></i>
                <span
                  class="d-inline-block text-truncate line-height-xl"
                  :class="{ 'opacity-75' : !data.item.visibility }"
                  style="max-width: 250px;">{{ data.item.name }}</span>
              </div>
            </template>
            <template #cell(ip)="data">
              <b-button
                v-wave
                size="sm"
                variant="hover-light-info"
                class="font-weight-normal py-1 px-2"
                @click="copyInput(`#copy-ip-${data.item._id}`)">
                {{ data.item.ip }}
                <i class="far fa-copy fa-sm ml-2 p-0" />
              </b-button>
              <b-input
                :id="`copy-ip-${data.item._id}`"
                :value="data.item.ip"
                class="position-absolute"
                style="opacity: 0.01; z-index: -1000; left: -9999px;" />
            </template>
            <template #cell(country)="data">
              <div class="d-flex align-items-center">
                <img
                  v-b-tooltip.hover
                  :title="countryName(data.item.country)"
                  :src="countryFlag(data.item.country)"
                  :alt="data.item.country"
                  class="w-20px mr-2 no-select rounded-sm bg-light border border-light rounded-sm rounded-sm" />
                <span
                  class="d-inline-block mr-1 text-truncate line-height-xl"
                  style="max-width: 180px;">{{ data.item.location }}</span>
              </div>
            </template>
            <template #cell(active)="data">
              <div class="d-flex justify-center-start justify-content-lg-center">
                <!-- Wrapper for fake element + checkbox -->
                <div
                  class="position-relative"
                  style="width: fit-content;">
                  <!-- Fake element above checkbox that TRIGGERS event -->
                  <div
                    class="w-100 h-100 position-absolute"
                    style="z-index: 20;"
                    role="button"
                    @click="handleStatusChange(data.item)" />
                  <!-- Checkbox that REPRESENTS current status, not changes it -->
                  <b-form-checkbox
                    :checked="data.item.active == 'on'"
                    size="lg"
                    switch
                    name="switch-vpn-server"
                    class="switch-success ml-0 ml-md-3 mb-1 mb-md-2" />
                </div>
              </div>
            </template>
            <template #cell(vpn_servers)="data">
              <div class="d-flex">
                <div v-if="!serverVpnServersEmpty(data.item._id)">
                  <div class="table-grid-items">
                    <span
                      v-for="vpnServer in serverVpnServers(data.item._id)"
                      :key="vpnServer._id"
                      v-b-tooltip.hover
                      :title="vpnServer.name"
                      class="svg-icon svg-icon-sm cursor-help">
                      <inline-svg
                        :src="`media/svg/icons/VPN/${vpnServer.code}.svg`"
                        class="svg-icon m-0" />
                    </span>
                  </div>
                </div>
                <i
                  v-else
                  class="fa fa-minus fa-xs opacity-50" />
              </div>
            </template>
            <template #cell(extras)="data">
              <div class="table-grid-items">
                <!-- rmq queue -->
                <div>
                  <span
                    :id="`rmq_queue-${data.item._id}`"
                    :disabled="!serverHasRmqQueue(data.item._id)"
                    class="svg-icon svg-icon-sm"
                    @click="
                      serverHasRmqQueue(data.item._id)
                        ? copyInput(`#copy-rmq_queue-${data.item._id}`)
                        : false">
                    <inline-svg
                      src="media/svg/icons/Code/RabbitMQ.svg"
                      class="svg-icon m-0"
                      :class="[
                        {  'opacity-40' : !serverHasRmqQueue(data.item._id) },
                        {  'cursor-pointer' : serverHasRmqQueue(data.item._id) },
                      ]" />
                  </span>
                  <b-tooltip :target="`rmq_queue-${data.item._id}`">
                    <div class="mb-2 font-weight-bold line-height-sm no-select">
                      RabbitMQ queue:
                    </div>
                    <div class="font-size-sm mb-3 px-2 py-1 rounded border bg-light">
                      {{ data.item.rmq_queue }}
                    </div>
                    <div class="font-size-xs opacity-75 no-select">(click icon to copy)</div>
                  </b-tooltip>
                  <b-input
                    :id="`copy-rmq_queue-${data.item._id}`"
                    :value="data.item.rmq_queue"
                    class="position-absolute"
                    style="opacity: 0.01; z-index: -1000; left: -9999px;" />
                </div>
                <!-- cloudflare_proxy -->
                <div>
                  <span
                    v-b-tooltip.hover
                    :title="data.item.cloudflare_proxy ? 'Cloudflare Active' : 'Cloudflare Disabled'"
                    class="svg-icon svg-icon-sm cursor-help">
                    <inline-svg
                      src="media/svg/icons/Code/Cloudflare.svg"
                      class="svg-icon m-0"
                      :class="{ 'opacity-40' : !data.item.cloudflare_proxy }" />
                  </span>
                </div>
              </div>
            </template>
            <template #cell(group_recipe)="data">
              <div v-if="!serverRecipeGroupEmpty(data.item._id)">
                <i class="fas fa-folder-open fa-sm"></i>
                {{ data.item.group_recipe.name }}
              </div>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(group)="data">
              <ul
                v-if="!serverGroupsEmpty(data.item._id)"
                class="list-unstyled mb-0">
                <li
                  v-for="group in data.item.group"
                  :key="group._id"
                  class="my-2">
                  <i class="fas fa-folder-open fa-sm"></i>
                  {{ group.name }}
                </li>
              </ul>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(client_version)="data">
              <span v-if="data.item.client_version">
                <span class="font-size-xs">v</span>
                <span>{{ data.item.client_version }}</span>
              </span>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(edit)="data">
              <b-dropdown
                :boundary="serversListLength < 5 ? 'window' : 'scrollParent'"
                no-caret
                right
                variant="outline-primary"
                toggle-class="btn-icon"
                size="sm"
                class="mr-4 dropdown-inline">
                <template v-slot:button-content>
                  <i class="fa fa-ellipsis-h fa-sm"></i>
                </template>
                <b-dropdown-item-button
                  :disabled="serversResponses('getServer') || recipesResponses('recipesList') || recipeGroupsResponses('recipesGroupsList')"
                  @click="showModal('manageServerModal', data.item._id)">
                  <i class="fa fa-pen fa-sm mr-3"></i>
                  <span>Edit server</span>
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="serversResponses('getServer') || recipesResponses('recipesList')"
                  @click="showModal('installRecipeModal', data.item._id)">
                  <i class="fa fa-download fa-sm mr-3"></i>
                  <span>Install recipe</span>
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  @click="$router.push({
                    name: 'ServersVpnManage',
                    params: { id: data.item._id }
                  }).catch(() => {})">
                  <i class="fa fa-shield-alt fa-sm mr-3"></i>
                  <span>Manage VPN servers</span>
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  @click="$router.push({
                    name: 'ServersCloudflare',
                    params: { id: data.item._id }
                  }).catch(() => {})">
                  <i class="fa fa-cloud fa-sm mr-3"></i>
                  <span>Configure Cloudflare</span>
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="serversResponses('getServerToken')"
                  @click="
                    selectServer(data.item),
                    showModal('serverToken', data.item._id)
                  ">
                  <i class="fa fa-key fa-sm mr-3"></i>
                  <span>Show access token</span>
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="serversResponses('getServer') || macrosesResponses('getMacroses')"
                  @click="showModal('serverMacroses', data.item._id)">
                  <i class="fa fa-code fa-sm mr-3"></i>
                  <span>Manage macroses</span>
                </b-dropdown-item-button>
                <!-- visibility -->
                <b-dropdown-item-button
                  v-if="data.item.visibility"
                  :disabled="serversResponses('switchServerVisibility')"
                  @click="
                    selectServer(data.item),
                    showModal('switchServerVisibility', data.item._id)
                  ">
                  <i class="fa fa-eye-slash fa-sm mr-3"></i>
                  <span>Hide server</span>
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  v-else
                  :disabled="serversResponses('switchServerVisibility')"
                  @click="switchServerVisibility(data.item._id, 'show')">
                  <i class="fa fa-eye fa-sm mr-3"></i>
                  <span>Show server</span>
                </b-dropdown-item-button>
                <!-- // visibility -->
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item-button
                  variant="danger"
                  :disabled="serversResponses('getServer') || serversResponses('resetServer')"
                  @click="
                    selectServer(data.item),
                    showModal('resetServer')
                  ">
                  <i class="fa fa-redo-alt fa-sm mr-3 text-danger opacity-75"></i>
                  Reset server
                </b-dropdown-item-button>
              </b-dropdown>
              <b-button
                v-wave
                :id="`deleteButton-${data.item._id}`"
                size="sm"
                variant="outline-primary"
                class="btn-icon"
                :disabled="serversResponses('deleteServer')"
                @click="
                  selectServer(data.item),
                  showDeletePopover(data.item._id)
                ">
                <i class="fa fa-trash fa-sm"></i>
              </b-button>
            </template>

            <!-- empty state -->
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>

          <div class="w-100 d-flex align-items-center flex-wrap pt-6">
            <b-dropdown
              variant="hover-light-primary"
              id="custiomize-table"
              no-caret
              size="sm"
              class="mr-auto">
              <template #button-content>
                <span>Customize Table</span>
                <i class="fa fa-cog fa-sm pl-2 pr-0"></i>
              </template>
              <div class="d-flex flex-column px-3">
                <b-form-checkbox
                  v-for="field in fields"
                  :key="`dropdown-item-${field.key}`"
                  v-model="field.visible"
                  :disabled="field.default"
                  size="md"
                  class="my-1">
                  {{ field.label }}
                </b-form-checkbox>
              </div>
            </b-dropdown>
            <div class="d-flex align-items-center">
              <div class="d-flex align-items-center mr-2">
                <span class="font-weight-bold font-size-sm">Per Page:</span>
                <b-form-select
                  size="sm"
                  :value="serversLimit"
                  :options="limits"
                  class="w-auto ml-2"
                  @change="
                    setServersGetParam(1, 'page'),
                    setServersGetParam($event, 'limit')
                  " />
              </div>
              <!--
                Don't remove v-if statement!
                https://github.com/bootstrap-vue/bootstrap-vue/issues/3372#issuecomment-596158197
              -->
              <b-pagination
                v-if="!serversEmpty"
                :value="serversPage"
                :total-rows="totalServers"
                :per-page="serversLimit"
                size="md"
                first-number
                last-number
                class="mb-0 ml-auto"
                @change="setServersGetParam($event, 'page')">
                <template #prev-text>
                  <i class="fa fa-xs fa-chevron-left"></i>
                </template>
                <template #next-text>
                  <i class="fa fa-xs fa-chevron-right"></i>
                </template>
              </b-pagination>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-popover
      v-for="server in serversList"
      :key="`popover-${server._id}`"
      :target="`deleteButton-${server._id}`"
      :ref="`popover-${server._id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete «<span class="font-weight-bolder">{{ server.name }}</span>» server?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(server._id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmDelete(), hideDeletePopover(server._id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>

    <modal-manage-server />
    <modal-install-recipe />
    <modal-shutdown-server />
    <modal-open-ssh-key />
    <modal-server-macroses />
    <modal-reset-server />
    <modal-server-token />
    <modal-switch-visibility />
    
  </div>
</template>

<script>
import {
  // actions
  GET_SERVERS,
  GET_SERVER,
  SWITCH_SERVER,
  DELETE_SERVER,
  GET_OPENSSH_KEY,
  GET_SERVER_TOKEN,
  SWITCH_SERVER_VISIBILITY,

  // mutations
  SELECT_SERVER,
  UNSELECT_SERVER,
  CLEAR_SERVERS,
  SET_SERVERS_GET_PARAMS,
  SET_SERVERS_GET_PARAM,
  CLEAR_SERVERS_GET_PARAMS,

  // constans
  DEFAULT_SERVERS_GET_PARAMS,
} from '@/core/services/store/servers.module'
import { GET_RECIPES } from '@/core/services/store/recipes.module'
import { GET_GROUPS } from '@/core/services/store/groups.module'
import { GET_RECIPES_GROUPS, CLEAR_RECIPES_GROUPS } from '@/core/services/store/recipes-groups.module'
import { CLEAR_MACROSES, GET_MACROSES } from '@/core/services/store/macroses.module'
import { GET_COUNTRIES } from '@/core/services/store/countries.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { regex_digits } from '@/core/regex'
import {
  showToast,
  copyInput,
  showDeletePopover,
  hideDeletePopover,
  restoreTableColumns
} from '@/core/utils'
import ModalManageServer from './components/ModalManageServer.vue'
import ModalInstallRecipe from './components/ModalInstallRecipe.vue'
import ModalShutdownServer from './components/ModalShutdownServer.vue'
import ModalOpenSshKey from './components/ModalOpenSshKey.vue'
import ModalServerMacroses from './components/ModalServerMacroses.vue'
import ModalResetServer from './components/ModalResetServer.vue'
import ModalServerToken from './components/ModalServerToken.vue'
import ModalSwitchVisibility from './components/ModalSwitchVisibility.vue'
import SortButton from '@/view/layout/controls/SortButton.vue'

export default {
  name: 'ServersList',
  components: {
    ModalManageServer,
    ModalInstallRecipe,
    ModalShutdownServer,
    ModalOpenSshKey,
    ModalServerMacroses,
    ModalResetServer,
    ModalServerToken,
    ModalSwitchVisibility,
    SortButton,
  },
  data() {
    return {
      fields: [
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'font-weight-bold', 'pl-5'],
          visible: true,
          default: true,
        },
        {
          label: 'Location',
          key: 'country',
          class: ['align-middle', 'text-nowrap'],
          visible: true,
          default: true,
        },
        {
          label: 'IP',
          key: 'ip',
          class: ['align-middle', 'text-nowrap', 'font-size-sm', 'pl-0'],
          visible: true,
          default: true,
        },
        {
          label: 'Status',
          key: 'active',
          class: ['align-middle', 'text-nowrap', 'text-center'],
          visible: true,
        },
        {
          label: 'Cl. Version',
          key: 'client_version',
          class: ['align-middle', 'text-nowrap', 'font-size-sm'],
          visible: true,
        },
        {
          label: 'VPN Servers',
          key: 'vpn_servers',
          class: ['align-middle', 'text-nowrap'],
          visible: true,
        },
        {
          label: 'Recipes Group',
          key: 'group_recipe',
          class: ['align-middle', 'text-nowrap', 'font-size-sm'],
          width: '100px',
          visible: true,
        },
        {
          label: 'Groups',
          key: 'group',
          class: ['align-middle', 'text-nowrap', 'font-size-sm'],
          visible: true,
        },
        {
          label: 'Extras',
          key: 'extras',
          class: ['align-middle', 'text-nowrap'],
          visible: true,
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-lg-right', 'text-nowrap', 'pr-5'],
          visible: true,
          default: true,
        }
      ],
      limits: [10, 25, 50, 100],
      sortClickCount: 0,

      showToast,
      copyInput,
      showDeletePopover,
      hideDeletePopover,
      restoreTableColumns,
    }
  },
  computed: {
    ...mapGetters([
      'countryFlag',
      'countryName',

      'serversList',
      'serversListLength',
      'serversEmpty',
      'serversGetParams',
      'serversPage',
      'serversLimit',
      'totalServers',
      'totalServersPages',
      'selectedServer',
      'selectedServerId',
      'serverVpnServers',
      'serverVpnServersEmpty',
      'serverGroupsEmpty',
      'serverRecipeGroupEmpty',
      'serverHasRmqQueue',
      'serversResponses',
      'recipesResponses',
      'recipeGroupsResponses',
      'macrosesResponses',
    ]),

    visibleFields() {
      return this.fields.filter(field => field.visible)
    }
  },
  watch: {
    serversGetParams: {
      /* eslint-disable-next-line no-unused-vars */
      handler: function(newVal, oldVal) {
        this.$router.push({
          name: this.$options.name,
          query: newVal
        }).catch(() => {})
      },
      deep: true,
    },

    $route() {
      this.handleUrl()
    },

    visibleFields() {
      localStorage.setItem('serverTableColumns', JSON.stringify(this.fields))
    },

    /* eslint-disable-next-line no-unused-vars */
    serversEmpty(newVal, oldVal) {
      if (newVal && this.serversPage > this.totalServersPages) {
        this.setServersGetParam(this.totalServersPages, 'page')
        this.$router.push({
          name: this.$options.name,
          query: { ...this.$route.query, page: this.totalServersPages === 0 ? 1 : this.totalServersPages }
        }).catch(() => {})
        this.getServers()
      }
    },
  },
  created() {
    this.$store.dispatch(GET_COUNTRIES)
    this.$store.commit(CLEAR_SERVERS)

    this.restoreTableColumns('serverTableColumns')
    this.handleUrl()

    eventBus.$on('showToast/Servers', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    eventBus.$off('showToast/Servers')

    this.$store.commit(CLEAR_SERVERS_GET_PARAMS)
  },
  methods: {
    validateQueryParams(queryParams) {
      const allowedKeys = Object.keys(DEFAULT_SERVERS_GET_PARAMS)

      // Check if there are no extra query parameters
      const extraKeys = Object.keys(queryParams).filter(
        key => !allowedKeys.includes(key)
      )

      // Validate each query parameter
      const validLimit = this.limits.includes(Number(queryParams.limit))
      const validPage = regex_digits.test(queryParams.page)
      const validSort = [
        'name',
        'ip',
        'country',
        'created'
      ].includes(queryParams.sort)
      const validOrd = ['asc', 'desc'].includes(queryParams.ord)

      return (
        extraKeys.length === 0 &&
        validLimit &&
        validPage &&
        validSort &&
        validOrd
      )
    },

    handleUrl() {
      const queryParams = this.$route.query
      const valid = this.validateQueryParams(queryParams)

      if (valid) {
        this.applyUrlQueries()
      } else {
        this.clearUrlQueries()
      }

      this.getServers()
    },

    applyUrlQueries() {
      this.$store.commit(SET_SERVERS_GET_PARAMS, this.$route.query)
    },

    clearUrlQueries() {
      this.$store.commit(CLEAR_SERVERS_GET_PARAMS)
      this.$router.push({
        name: this.$options.name,
        query: {}
      }).catch(() => {})
    },

    getServers() {
      this.$store.dispatch(GET_SERVERS)
    },

    setServersGetParam(value, param) {
      this.$store.commit(SET_SERVERS_GET_PARAM, { value, param })
    },

    handleSortDirection(key) {
      if (key === this.serversGetParams.sort) {
        if (this.sortClickCount === 2) {
          // The user has clicked the same key three times, so force default sorting.
          this.setServersGetParam('desc', 'ord')
          this.setServersGetParam('created', 'sort') // Default sort by "created" and "desc".
          this.sortClickCount = 0 // Reset the click count.
        } else {
          // Toggle between 'asc' and 'desc'.
          this.setServersGetParam(
            this.serversGetParams.ord === 'desc' ? 'asc' : 'desc',
            'ord'
          )
          this.sortClickCount++
        }
      } else {
        // The user clicked a different key, reset the click count and update the sorting key.
        this.sortClickCount = 1
        this.setServersGetParam('asc', 'ord')
        this.setServersGetParam(key, 'sort')
      }
    },

    selectServer(server) {
      this.$store.commit(SELECT_SERVER, server)
    },

    unselectServer() {
      this.$store.commit(UNSELECT_SERVER)
    },

    dataIsReady(data) {
      let result = []
      for (let i = 0; i < data.length; i++) {
        result.push(Array.isArray(data[i]) || typeof data == 'object')
      }
      return result.every(el => el)
    },

    showModal(type, server_id) {
      let getServer = () => {
        if (!server_id) {
          return true
        }
        return this.$store
          .dispatch(GET_SERVER, { id: server_id })
          .then(data => {
            this.selectServer(data)
          })
      }

      let requirements = {
        openSshKey: [
          () => this.$store.dispatch(GET_OPENSSH_KEY)
        ],
        serverToken: [
          () => this.$store.dispatch(GET_SERVER_TOKEN, { server_id })
        ],
        manageServerModal: [
          getServer,
          () => this.$store.dispatch(GET_GROUPS),
          () => {
            this.$store.commit(CLEAR_RECIPES_GROUPS)
            this.$store.dispatch(GET_RECIPES_GROUPS, { action: 'start' })
          }
        ],
        installRecipeModal: [
          getServer,
          () => this.$store.dispatch(GET_RECIPES)
        ],
        serverMacroses: [
          getServer,
          () => this.$store.commit(CLEAR_MACROSES),
          () => this.$store.dispatch(GET_MACROSES, { server_id })
        ],
      }

      if (requirements.hasOwnProperty(type)) {
        Promise
          .all(requirements[type].map(promiseFn => promiseFn()))
          .then(res => {
            if (this.dataIsReady(res)) {
              eventBus.$emit('openModal', type)
            }
          })
      } else {
        eventBus.$emit('openModal', type)
      }
    },

    confirmDelete() {
      this.$store.dispatch(DELETE_SERVER)
    },

    handleStatusChange(server) {
      this.selectServer(server)

      if (server.active == 'on') {
        eventBus.$emit('openModal', 'shutdownServerConfirm')
      } else {
        this.$store.dispatch(SWITCH_SERVER, { id: server._id })
      }
    },

    rmqQueueTooltip(queue) {
      return `<div class="mb-2 line-height-sm no-select">RabbitMQ Queue:</div><div class="font-weight-bold mb-3 p-2 rounded border bg-light line-height-sm">${queue}</div><div class="font-size-xs opacity-75 no-select">(click to copy)</div>`
    },

    switchServerVisibility(id, action) {
      this.$store.dispatch(SWITCH_SERVER_VISIBILITY, { id, action })
    }
  }
}
</script>
